package com.partejar.app.feature.relacionamentos.presentation

import com.partejar.app.core.ui.domain.address.PatientAddressFields

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.Crossfade
import androidx.compose.animation.EnterExitState
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.partejar.app.core.format.formatBrazilianDate
import kotlinx.datetime.LocalDateTime
import com.partejar.app.core.format.formatBrazilianDateTime
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.home.data.currentHomeDateTime
import com.partejar.app.feature.relacionamentos.data.*
import kotlinx.coroutines.flow.first

@Composable
internal fun RelacionamentoDetailContent(
    item: RelationshipDetail,
    error: String?,
    busyAction: RelationshipDetailAction?,
    interactionExpanded: Boolean,
    interactionKind: InteractionKind,
    interactionNote: String,
    nextFollowUp: String?,
    onEditPerson: () -> Unit,
    onInteractionExpandedChange: (Boolean) -> Unit,
    onInteractionKindChange: (InteractionKind) -> Unit,
    onInteractionNoteChange: (String) -> Unit,
    onNextFollowUpChange: (String?) -> Unit,
    onPickFollowUp: () -> Unit,
    onRegisterInteraction: () -> Unit,
    onScheduleMeeting: () -> Unit,
    onMove: (RelacionamentoStage) -> Unit,
    onStartContract: () -> Unit,
    now: LocalDateTime = currentLocalDateTime()
) {
    val spacing = PartejarTheme.spacing
    val readOnly = item.stage == RelacionamentoStage.LOST
    val busy = busyAction != null
    val guidance = relationshipNowGuidance(item, now)
    var stageSheetOpen by rememberSaveable(item.id) { mutableStateOf(false) }
    var closeConfirmationOpen by rememberSaveable(item.id) { mutableStateOf(false) }

    Column(Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = spacing.space16, vertical = spacing.space12)
                .then(
                    if (readOnly) Modifier.windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                    else Modifier
                ),
            verticalArrangement = Arrangement.spacedBy(spacing.space20)
        ) {
            RelationshipHeader(item = item, readOnly = readOnly, onEdit = onEditPerson)

            if (readOnly) {
                PartejarAlert("Relacionamento encerrado", PartejarAlertTone.INFO) {
                    Text("Marcado como perdido e disponível somente para consulta.")
                }
            }

            error?.takeUnless { interactionExpanded }?.let {
                PartejarAlert("Não foi possível concluir", PartejarAlertTone.ERROR) { Text(it) }
            }

            RelationshipStageSection(
                stage = item.stage,
                canChange = isPreContract(item.stage),
                busy = busy,
                onChangeStage = { stageSheetOpen = true }
            )

            if (!readOnly) {
                RelationshipNowSection(
                    guidance = guidance,
                    busy = busy,
                    onMove = onMove,
                    onStartContract = onStartContract
                )
            }

            if (!readOnly && !guidance.offersContract) {
                RelationshipContractShortcut(stage = item.stage, busy = busy, onStartContract = onStartContract)
            }

            RelationshipHistory(item.interactions, item.stageHistory, now)

            RelationshipDataSection(item = item, readOnly = readOnly, onEdit = onEditPerson)
        }

        if (!readOnly) {
            RelationshipActionBar(
                hasMeeting = item.nextMeeting != null,
                busy = busy,
                meetingSubmitting = busyAction == RelationshipDetailAction.MEETING,
                onRegisterConversation = { onInteractionExpandedChange(true) },
                onScheduleMeeting = onScheduleMeeting
            )
        }
    }

    if (interactionExpanded && !readOnly) {
        RegisterConversationSheet(
            name = item.name,
            kind = interactionKind,
            note = interactionNote,
            nextFollowUp = nextFollowUp,
            submitting = busyAction == RelationshipDetailAction.INTERACTION,
            error = error,
            now = now,
            onKindChange = onInteractionKindChange,
            onNoteChange = onInteractionNoteChange,
            onNextFollowUpChange = onNextFollowUpChange,
            onPickFollowUp = onPickFollowUp,
            onDismiss = { onInteractionExpandedChange(false) },
            onSave = onRegisterInteraction
        )
    }

    if (stageSheetOpen && isPreContract(item.stage)) {
        RelationshipStageSheet(
            stage = item.stage,
            busy = busy,
            onDismiss = { stageSheetOpen = false },
            onMove = { target ->
                stageSheetOpen = false
                onMove(target)
            },
            onStartContract = {
                stageSheetOpen = false
                onStartContract()
            },
            onCloseRequest = {
                stageSheetOpen = false
                closeConfirmationOpen = true
            }
        )
    }

    if (closeConfirmationOpen) {
        AlertDialog(
            onDismissRequest = { closeConfirmationOpen = false },
            title = { Text("Encerrar como perdido?") },
            text = { Text("${item.name} sai da lista ativa e o relacionamento fica salvo só para consulta. Não é possível reabrir depois.") },
            confirmButton = {
                PartejarButton(
                    "Encerrar",
                    {
                        closeConfirmationOpen = false
                        onMove(RelacionamentoStage.LOST)
                    },
                    variant = PartejarButtonVariant.DESTRUCTIVE,
                    size = PartejarButtonSize.COMPACT,
                    fillWidth = false,
                    shape = PartejarShapes.pill
                )
            },
            dismissButton = {
                PartejarButton(
                    "Cancelar",
                    { closeConfirmationOpen = false },
                    variant = PartejarButtonVariant.TERTIARY,
                    size = PartejarButtonSize.COMPACT,
                    fillWidth = false,
                    shape = PartejarShapes.pill
                )
            }
        )
    }
}

internal enum class RelationshipNowAction { ADVANCE_TO_WAITING, START_CONTRACT, OPEN_CONTRACT }

internal data class RelationshipNowGuidance(
    val title: String,
    val description: String,
    val tone: RelationshipHintTone,
    val kind: RelationshipHintKind,
    val action: RelationshipNowAction? = null
) {
    val offersContract: Boolean
        get() = action == RelationshipNowAction.START_CONTRACT || action == RelationshipNowAction.OPEN_CONTRACT
}

/** One answer to "what do I do now?". The bottom bar always offers conversation and meeting, so this never repeats them. */
internal fun relationshipNowGuidance(item: RelationshipDetail, now: LocalDateTime): RelationshipNowGuidance {
    val lastFollowUp = item.interactions.maxByOrNull(Interaction::occurredAt)?.nextFollowUp
    val hint = relationshipHint(item.stage, item.needsAttention, item.nextMeeting, lastFollowUp, now)
    return when (hint.kind) {
        RelationshipHintKind.MEETING -> RelationshipNowGuidance(
            title = "Reunião de apresentação",
            description = hint.text.removePrefix("Reunião ").replaceFirstChar(Char::uppercase),
            tone = hint.tone,
            kind = hint.kind
        )
        RelationshipHintKind.FOLLOW_UP -> if (hint.tone == RelationshipHintTone.ATTENTION) {
            RelationshipNowGuidance(
                title = "Retome o contato",
                description = "${hint.text}. Converse com ela e registre como foi.",
                tone = hint.tone,
                kind = hint.kind
            )
        } else {
            RelationshipNowGuidance(
                title = "Retorno marcado",
                description = hint.text.removePrefix("Retorno ").replaceFirstChar(Char::uppercase),
                tone = hint.tone,
                kind = hint.kind
            )
        }
        RelationshipHintKind.STALE -> RelationshipNowGuidance(
            title = "Retome o contato",
            description = "Faz tempo que vocês não conversam. Converse com ela e registre como foi.",
            tone = hint.tone,
            kind = hint.kind
        )
        RelationshipHintKind.CONTRACT -> RelationshipNowGuidance(
            title = "Continue o contrato",
            description = "Revise os dados, gere o documento e envie o link de assinatura.",
            tone = hint.tone,
            kind = hint.kind,
            action = RelationshipNowAction.OPEN_CONTRACT
        )
        RelationshipHintKind.CLOSED -> RelationshipNowGuidance(
            title = "Relacionamento encerrado",
            description = stageGuidance(item.stage),
            tone = hint.tone,
            kind = hint.kind
        )
        RelationshipHintKind.NONE -> when (item.stage) {
            RelacionamentoStage.CAPTURE -> RelationshipNowGuidance(
                title = "Marque a reunião de apresentação",
                description = "Use “Agendar reunião” aqui embaixo. Enquanto isso, registre as conversas.",
                tone = hint.tone,
                kind = RelationshipHintKind.MEETING
            )
            RelacionamentoStage.MEETING -> RelationshipNowGuidance(
                title = "A reunião já aconteceu?",
                description = "Se a família está decidindo, avance para o fechamento.",
                tone = hint.tone,
                kind = hint.kind,
                action = RelationshipNowAction.ADVANCE_TO_WAITING
            )
            RelacionamentoStage.WAITING -> RelationshipNowGuidance(
                title = "Ela já decidiu?",
                description = "Se vai contratar, inicie o contrato. Se ainda está pensando, marque um retorno ao registrar a conversa.",
                tone = hint.tone,
                kind = hint.kind,
                action = RelationshipNowAction.START_CONTRACT
            )
            else -> RelationshipNowGuidance(
                title = "Nenhum próximo passo marcado",
                description = stageGuidance(item.stage),
                tone = hint.tone,
                kind = hint.kind
            )
        }
    }
}

@Composable
private fun RelationshipHeader(item: RelationshipDetail, readOnly: Boolean, onEdit: () -> Unit) {
    val spacing = PartejarTheme.spacing
    val details = listOfNotNull(
        item.babyName?.takeIf(String::isNotBlank)?.let { "Bebê $it" },
        item.dueDate?.let { "DPP ${formatBrazilianDate(it) ?: it}" },
        item.maternity?.takeIf(String::isNotBlank)
    ).joinToString(" · ")

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(spacing.space12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        RelationshipAvatar(item.name, spacing.space48 + spacing.space8)
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            Text(item.name, style = MaterialTheme.typography.headlineSmall)
            if (details.isNotEmpty()) {
                Text(details, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
        if (!readOnly) {
            PartejarIconButton(onClick = onEdit) {
                Icon(PartejarIcons.Actions.edit, contentDescription = "Editar dados da pessoa")
            }
        }
    }
}

@Composable
private fun RelationshipStageSection(
    stage: RelacionamentoStage,
    canChange: Boolean,
    busy: Boolean,
    onChangeStage: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "Em que ponto está",
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.weight(1f).semantics { heading() }
                )
                if (canChange) {
                    PartejarButton(
                        text = "Mudar etapa",
                        onClick = onChangeStage,
                        enabled = !busy,
                        variant = PartejarButtonVariant.TERTIARY,
                        size = PartejarButtonSize.COMPACT,
                        fillWidth = false
                    )
                }
            }
            if (stage != RelacionamentoStage.LOST) {
                RelationshipJourneyStepper(stage)
            }
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                color = MaterialTheme.colorScheme.surfaceContainer
            ) {
                Column(
                    modifier = Modifier.padding(spacing.space12),
                    verticalArrangement = Arrangement.spacedBy(spacing.space4)
                ) {
                    Text(stageTitle(stage), style = MaterialTheme.typography.titleSmall)
                    Text(stageGuidance(stage), style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

@Composable
private fun RelationshipNowSection(
    guidance: RelationshipNowGuidance,
    busy: Boolean,
    onMove: (RelacionamentoStage) -> Unit,
    onStartContract: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val colors = relationshipToneColors(guidance.tone)
    Surface(
        modifier = Modifier.fillMaxWidth().semantics(mergeDescendants = false) { liveRegion = LiveRegionMode.Polite },
        shape = MaterialTheme.shapes.large,
        color = colors.container,
        contentColor = colors.content
    ) {
        Column(
            modifier = Modifier.padding(spacing.space16),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            Text(
                "O que fazer agora",
                style = MaterialTheme.typography.labelLarge,
                modifier = Modifier.semantics { heading() }
            )
            Row(
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    modifier = Modifier.size(spacing.space48),
                    shape = PartejarShapes.radius14,
                    color = MaterialTheme.colorScheme.surface,
                    contentColor = colors.accent
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            relationshipHintIcon(guidance.kind),
                            contentDescription = null,
                            modifier = Modifier.size(PartejarTheme.dimensions.iconMedium)
                        )
                    }
                }
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                    Text(guidance.title, style = MaterialTheme.typography.titleMedium)
                    Text(guidance.description, style = MaterialTheme.typography.bodyMedium)
                }
            }
            when (guidance.action) {
                RelationshipNowAction.ADVANCE_TO_WAITING -> PartejarButton(
                    text = "Avançar para fechamento",
                    onClick = { onMove(RelacionamentoStage.WAITING) },
                    enabled = !busy,
                    variant = PartejarButtonVariant.SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill
                )
                RelationshipNowAction.START_CONTRACT -> PartejarButton(
                    text = "Iniciar contrato",
                    onClick = onStartContract,
                    enabled = !busy,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill,
                    leadingIcon = {
                        Icon(PartejarIcons.Relationships.contract, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                    }
                )
                RelationshipNowAction.OPEN_CONTRACT -> PartejarButton(
                    text = "Abrir contrato",
                    onClick = onStartContract,
                    enabled = !busy,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill,
                    leadingIcon = {
                        Icon(PartejarIcons.Relationships.contract, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                    }
                )
                null -> Unit
            }
        }
    }
}

@Composable
private fun RelationshipContractShortcut(stage: RelacionamentoStage, busy: Boolean, onStartContract: () -> Unit) {
    val spacing = PartejarTheme.spacing
    val continuing = stage == RelacionamentoStage.CONTRACT
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.background,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, MaterialTheme.colorScheme.outline)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = spacing.space16, vertical = spacing.space12),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(if (continuing) "Contrato em andamento" else "Ela decidiu contratar?", style = MaterialTheme.typography.titleSmall)
                Text(
                    if (continuing) "Revise os dados e continue o contrato." else "Você pode iniciar o contrato em qualquer etapa.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            PartejarButton(
                text = if (continuing) "Abrir contrato" else "Iniciar contrato",
                onClick = onStartContract,
                enabled = !busy,
                variant = PartejarButtonVariant.SECONDARY,
                size = PartejarButtonSize.COMPACT,
                fillWidth = false,
                shape = PartejarShapes.pill
            )
        }
    }
}

@Composable
private fun RelationshipDataSection(item: RelationshipDetail, readOnly: Boolean, onEdit: () -> Unit) {
    val spacing = PartejarTheme.spacing
    val values = relationshipDetailData(item)
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "Dados da gestante",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.weight(1f).semantics { heading() }
                )
                if (!readOnly) {
                    PartejarButton(
                        text = "Editar",
                        onClick = onEdit,
                        variant = PartejarButtonVariant.TERTIARY,
                        size = PartejarButtonSize.COMPACT,
                        fillWidth = false
                    )
                }
            }
            if (values.isEmpty()) {
                Text(
                    "Nenhum dado informado ainda.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            } else {
                RelationshipDataGrid(values)
            }
        }
    }
}

@Composable
private fun RelationshipActionBar(
    hasMeeting: Boolean,
    busy: Boolean,
    meetingSubmitting: Boolean,
    onRegisterConversation: () -> Unit,
    onScheduleMeeting: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val fontScale = LocalDensity.current.fontScale
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, MaterialTheme.colorScheme.outlineVariant)
    ) {
        BoxWithConstraints(
            Modifier
                .fillMaxWidth()
                .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                .padding(horizontal = spacing.space16, vertical = spacing.space12)
        ) {
            val stacked = maxWidth < spacing.space64 * 4 + spacing.space24 || fontScale > 1.3f
            val register: @Composable (Modifier) -> Unit = { modifier ->
                PartejarButton(
                    text = "Registrar conversa",
                    onClick = onRegisterConversation,
                    modifier = modifier,
                    enabled = !busy,
                    fillWidth = stacked,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill,
                    leadingIcon = {
                        Icon(PartejarIcons.Relationships.whatsApp, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                    },
                    motionEnabled = true
                )
            }
            val meeting: @Composable (Modifier) -> Unit = { modifier ->
                val label = if (hasMeeting) "Reagendar reunião" else "Agendar reunião"
                PartejarButton(
                    text = if (stacked) label else label.substringBefore(' '),
                    onClick = onScheduleMeeting,
                    modifier = modifier.semantics { contentDescription = label },
                    enabled = !busy,
                    isLoading = meetingSubmitting,
                    fillWidth = stacked,
                    variant = PartejarButtonVariant.SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill,
                    leadingIcon = {
                        Icon(PartejarIcons.Relationships.meeting, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                    },
                    motionEnabled = true
                )
            }
            if (stacked) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                    register(Modifier)
                    meeting(Modifier)
                }
            } else {
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                    register(Modifier.weight(1.5f))
                    meeting(Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
internal fun RelationshipEditForm(
    item: RelationshipDetail,
    editing: Boolean,
    readOnly: Boolean,
    submitting: Boolean,
    contractAttempt: Int,
    onEditingChange: (Boolean) -> Unit,
    onSave: (RelationshipEdit) -> Unit
) {
    val spacing = PartejarTheme.spacing
    val reduced = reducedMotion()
    var name by remember(item.id, item.name) { mutableStateOf(item.name) }
    var baby by remember(item.id, item.babyName) { mutableStateOf(item.babyName.orEmpty()) }
    var maternity by remember(item.id, item.maternity) { mutableStateOf(item.maternity.orEmpty()) }
    var address by remember(item.id, item.address) { mutableStateOf(item.address) }
    var origin by remember(item.id, item.origin) { mutableStateOf(item.origin) }
    var dueDate by remember(
        item.id,
        item.dueDate
    ) { mutableStateOf(formatBrazilianDate(item.dueDate).orEmpty()) }
    var livesInBh by remember(item.id, item.livesInBh) { mutableStateOf(item.livesInBh) }
    val contractErrors = relationshipContractErrors(baby, parseBrazilianDate(dueDate)?.toString())
    val babyError = contractErrors.babyName.takeIf { contractAttempt > 0 }
    val dueDateError = when {
        dueDate.isNotBlank() && parseBrazilianDate(dueDate) == null -> "Informe uma DPP válida para continuar."
        contractAttempt > 0 -> contractErrors.dueDate
        else -> null
    }
    val babyFocus = remember { FocusRequester() }
    val dateFocus = remember { FocusRequester() }
    val babyVisibility = remember { BringIntoViewRequester() }
    val dateVisibility = remember { BringIntoViewRequester() }
    val formVisibility = remember { BringIntoViewRequester() }
    val shake = remember { Animatable(0f) }
    val shakeDistance = with(LocalDensity.current) { spacing.space4.toPx() }

    fun resetDrafts() {
        name = item.name
        baby = item.babyName.orEmpty()
        maternity = item.maternity.orEmpty()
        address = item.address
        origin = item.origin
        dueDate = formatBrazilianDate(item.dueDate).orEmpty()
        livesInBh = item.livesInBh
    }

    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        if (editing) {
            Text("Dados do relacionamento", style = MaterialTheme.typography.titleMedium)
        }
        AnimatedVisibility(
            visible = editing,
            enter = if (reduced) fadeIn(tween(PartejarMotion.press)) else fadeIn(
                tween(
                    PartejarMotion.entrance
                )
            ) + expandVertically(tween(PartejarMotion.entrance)),
            exit = if (reduced) fadeOut(tween(PartejarMotion.press)) else fadeOut(
                tween(
                    PartejarMotion.departure
                )
            ) + shrinkVertically(tween(PartejarMotion.departure))
        ) {
            LaunchedEffect(contractAttempt, editing, reduced) {
                shake.snapTo(0f)
                if (!editing || contractAttempt == 0) return@LaunchedEffect
                // Wait for the actual expansion, so scrolling/focus cannot target a clipped field.
                snapshotFlow {
                    transition.currentState == EnterExitState.Visible &&
                            transition.targetState == EnterExitState.Visible
                }.first { it }
                val errors =
                    relationshipContractErrors(baby, parseBrazilianDate(dueDate)?.toString())
                formVisibility.bringIntoView()
                when {
                    errors.babyName != null -> {
                        babyFocus.requestFocus()
                        babyVisibility.bringIntoView()
                    }

                    errors.dueDate != null -> {
                        dateFocus.requestFocus()
                        dateVisibility.bringIntoView()
                    }

                    else -> formVisibility.bringIntoView()
                }
                if (!reduced && errors.hasErrors) {
                    // Two small, decaying horizontal oscillations; never a spring or a loop.
                    for (target in listOf(1f, -1f, 0.5f, -0.5f, 0f)) {
                        shake.animateTo(
                            target,
                            tween(PartejarMotion.entrance / 5, easing = PartejarMotion.calm)
                        )
                    }
                }
            }
            PartejarCard(modifier = Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    if (contractAttempt > 0) {
                        Box(
                            Modifier.bringIntoViewRequester(formVisibility)
                                .semantics { liveRegion = LiveRegionMode.Polite }) {
                            contractErrors.summary?.let { message ->
                                PartejarAlert(
                                    "Complete os dados para continuar",
                                    PartejarAlertTone.ERROR
                                ) { Text(message) }
                            } ?: Text(
                                "Salve as alterações antes de continuar para o contrato.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(spacing.space12)
                    ) {
                        PartejarTextField(
                            value = name,
                            onValueChange = { name = it },
                            label = "Nome da gestante",
                            enabled = !submitting,
                            modifier = Modifier.weight(1f)
                        )
                        PartejarTextField(
                            value = baby,
                            onValueChange = { baby = it },
                            label = "Nome do bebê",
                            isError = babyError != null,
                            supportingText = babyError,
                            enabled = !submitting,
                            modifier = Modifier.weight(1f)
                                .focusRequester(babyFocus)
                                .bringIntoViewRequester(babyVisibility)
                                .graphicsLayer {
                                    translationX =
                                        if (!reduced && babyError != null) shake.value * shakeDistance else 0f
                                }
                        )
                    }

                    PartejarTextField(
                        value = maternity,
                        onValueChange = { maternity = it },
                        label = "Maternidade",
                        enabled = !submitting,
                        modifier = Modifier.fillMaxWidth()
                    )

                    RelationshipOriginField(origin, { origin = it }, enabled = !submitting)
                    Text("Endereço (opcional)", style = MaterialTheme.typography.titleMedium)
                    PatientAddressFields(address, { address = it }, enabled = !submitting)

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(spacing.space12)
                    ) {
                        PartejarDateField(
                            value = dueDate,
                            onValueChange = { dueDate = it },
                            label = "DPP",
                            enabled = !submitting,
                            error = dueDateError,
                            modifier = Modifier.weight(1f)
                                .focusRequester(dateFocus)
                                .bringIntoViewRequester(dateVisibility)
                                .graphicsLayer {
                                    translationX =
                                        if (!reduced && dueDateError != null) shake.value * shakeDistance else 0f
                                }
                        )
                        Row(
                            modifier = Modifier.weight(1f),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(spacing.space8)
                        ) {
                            Column(
                                Modifier.weight(1f),
                                verticalArrangement = Arrangement.spacedBy(spacing.space4)
                            ) {
                                Text("Mora em BH", style = MaterialTheme.typography.titleSmall)
                                Text(
                                    if (livesInBh) "Sim" else "Não",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            PartejarSwitch(
                                checked = livesInBh,
                                onCheckedChange = { livesInBh = it },
                                enabled = !submitting
                            )
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(spacing.space12)
                    ) {
                        PartejarButton(
                            text = "Cancelar",
                            onClick = { resetDrafts(); onEditingChange(false) },
                            variant = PartejarButtonVariant.TERTIARY,
                            enabled = !submitting,
                            motionEnabled = true,
                            size = PartejarButtonSize.COMPACT,
                            fillWidth = false,
                            modifier = Modifier.weight(1f),
                            shape = PartejarShapes.radius14
                        )
                        PartejarButton(
                            text = "Salvar alterações",
                            onClick = {
                                onSave(
                                    RelationshipEdit(
                                        name,
                                        baby.ifBlank { null },
                                        maternity.ifBlank { null },
                                        dueDate.takeIf(String::isNotBlank)
                                            ?.let { parseBrazilianDate(it)?.toString() },
                                        livesInBh,
                                        address,
                                        RelationshipOriginEdit.Set(origin)
                                    )
                                )
                            },
                            enabled = name.isNotBlank() && (dueDate.isBlank() || parseBrazilianDate(
                                dueDate
                            ) != null) &&
                                    (contractAttempt == 0 || !contractErrors.hasErrors),
                            isLoading = submitting,
                            motionEnabled = true,
                            size = PartejarButtonSize.COMPACT,
                            fillWidth = false,
                            modifier = Modifier.weight(1f),
                            shape = PartejarShapes.radius14
                        )
                    }
                }
            }
        }
        if (!editing) {
            PartejarCard(modifier = Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            "Dados do relacionamento",
                            style = MaterialTheme.typography.titleMedium,
                            modifier = Modifier.weight(1f)
                        )
                        if (!readOnly) {
                            PartejarButton(
                                text = "Editar",
                                onClick = { resetDrafts(); onEditingChange(true) },
                                variant = PartejarButtonVariant.TERTIARY,
                                leadingIcon = {
                                    Icon(
                                        PartejarIcons.Actions.edit,
                                        contentDescription = null,
                                        modifier = Modifier.size(PartejarTheme.dimensions.iconSmall)
                                    )
                                },
                                motionEnabled = true,
                                size = PartejarButtonSize.COMPACT,
                                fillWidth = false
                            )
                        }
                    }
                    RelationshipDataGrid(relationshipSummaryData(item))
                }
            }
        }
    }
}

private data class RelationshipDataValue(
    val label: String,
    val value: String,
    val icon: ImageVector
)

private fun patientMetadata(item: RelationshipDetail): List<RelationshipDataValue> = buildList {
    item.babyName?.takeIf(String::isNotBlank)
        ?.let { add(RelationshipDataValue("Bebê", it, PartejarIcons.Relationships.baby)) }
    item.dueDate?.let {
        add(
            RelationshipDataValue(
                "DPP",
                formatBrazilianDate(it) ?: it,
                PartejarIcons.Relationships.dueDate
            )
        )
    }
    add(
        RelationshipDataValue(
            "Mora em BH",
            if (item.livesInBh) "Sim" else "Não",
            PartejarIcons.Relationships.location
        )
    )
}

private fun relationshipDetailData(item: RelationshipDetail): List<RelationshipDataValue> =
    patientMetadata(item) + relationshipSummaryData(item)

private fun relationshipSummaryData(item: RelationshipDetail): List<RelationshipDataValue> = buildList {
    item.maternity?.takeIf(String::isNotBlank)?.let {
        add(
            RelationshipDataValue(
                "Maternidade",
                it,
                PartejarIcons.Relationships.maternity
            )
        )
    }
    item.prenatalType?.takeIf(String::isNotBlank)
        ?.let { add(RelationshipDataValue("Pré-natal", it, PartejarIcons.Relationships.prenatal)) }
    item.origin?.takeIf(String::isNotBlank)
        ?.let { add(RelationshipDataValue("Como chegou", relationshipOriginLabel(it), PartejarIcons.Relationships.origin)) }

    if (item.gestationalWeeks != null || item.gestationalDays != null) {
        val age = listOfNotNull(
            item.gestationalWeeks?.let { "$it semanas" },
            item.gestationalDays?.let { "$it dias" }).joinToString(" e ")
        val reported = item.gestationalReportedAt?.let {
            formatBrazilianDate(it) ?: formatBrazilianDateTime(it)
        }
        add(
            RelationshipDataValue(
                "IG informada",
                listOfNotNull(age, reported?.let { "em $it" }).joinToString(" · "),
                PartejarIcons.Clinical.calendar
            )
        )
    }
}

@Composable
private fun RelationshipDataGrid(values: List<RelationshipDataValue>) {
    val spacing = PartejarTheme.spacing
    val chunkedValues = values.chunked(2)

    Column {
        chunkedValues.forEachIndexed { rowIndex, rowValues ->
            Row(
                modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                horizontalArrangement = Arrangement.spacedBy(spacing.space16)
            ) {
                RelationshipDataCell(rowValues[0], Modifier.weight(1f))
                if (rowValues.size > 1) {
                    VerticalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    RelationshipDataCell(rowValues[1], Modifier.weight(1f))
                } else {
                    Spacer(Modifier.weight(1f))
                }
            }
            if (rowIndex < chunkedValues.lastIndex) {
                HorizontalDivider(
                    modifier = Modifier.padding(vertical = spacing.space12),
                    color = MaterialTheme.colorScheme.outlineVariant
                )
            }
        }
    }
}

@Composable
private fun RelationshipDataCell(value: RelationshipDataValue, modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier,
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.Top
    ) {
        Icon(
            value.icon,
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconSmall).padding(top = 2.dp),
            tint = MaterialTheme.colorScheme.primary
        )
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            Text(
                value.label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(value.value, style = MaterialTheme.typography.bodyMedium)
        }
    }
}
